import {noop} from '@github-ui/noop'
import type React from 'react'
import {createContext, useCallback, useContext, useMemo, useState} from 'react'

import type {CommitComment} from '../types/commit-types'

type InlineCommentsContextType = {
  comments: CommitComment[]
  addComments: (comments: CommitComment[]) => void
  findInlineComment: (relayId: string) => CommitComment | undefined
}

const InlineCommentsContext = createContext<InlineCommentsContextType>({
  comments: [],
  addComments: noop,
  findInlineComment: () => undefined,
})

export function InlineCommentsProvider({children}: React.PropsWithChildren) {
  const [comments, setComments] = useState<CommitComment[]>([])

  const addComments = useCallback(
    (newComments: CommitComment[]) => {
      const newCommentIds = newComments.map(comment => comment.id)
      const existingComments = comments.filter(comment => !newCommentIds.includes(comment.id))
      setComments([...existingComments, ...newComments])
    },
    [comments],
  )

  const findInlineComment = useCallback(
    (relayId: string) => {
      return comments.find(comment => comment.relayId === relayId)
    },
    [comments],
  )

  const inlineComments = useMemo(
    () => ({
      comments,
      addComments,
      findInlineComment,
    }),
    [addComments, comments, findInlineComment],
  )

  return <InlineCommentsContext.Provider value={inlineComments}>{children}</InlineCommentsContext.Provider>
}

export function useInlineComments() {
  const context = useContext(InlineCommentsContext)

  if (!context) {
    throw new Error('useInlineComments must be used within a InlineCommentsProvider')
  }

  return context
}

try{ InlineCommentsContext.displayName ||= 'InlineCommentsContext' } catch {}
try{ InlineCommentsProvider.displayName ||= 'InlineCommentsProvider' } catch {}